import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Tooltip,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { InfoOutline } from "@mui/icons-material";
import IOSSwitch from "../../../common/Switch";
import HPInput from "../../../common/Input";
import ApplyColorToAll from "../../ApplyColorToAll";

const COLOR_PREVIEW_MAP: Record<string, string> = {
  Orange: "#ffb09c",
  Green: "#57dfbe",
  Black: "#757679",
  Purple: "#a1a7df",
  Crimson: "#c36179",
  "Dark Green": "#57a68c",
  Blue: "#57bfe7",
  Yellow: "#ebdd7c",
  Multicolor:
    "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
  Gradient: "linear-gradient(90deg, #019EDB 0%, #00CF9C 100%)",
  Grayscale: "linear-gradient(90deg, #f0f0f0 0%, #404040 100%)",
  "Custom (per-bar)":
    "linear-gradient(90deg, #e57373 0%, #81c784 33%, #64b5f6 66%, #ffd54f 100%)",
};

const COLOR_OPTIONS = [
  { value: "Orange", color: "#ffb09c" },
  { value: "Green", color: "#57dfbe" },
  { value: "Blue", color: "#57bfe7" },
  { value: "Yellow", color: "#ebdd7c" },
  {
    value: "Multicolor",
    gradient:
      "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
  },
  {
    value: "Gradient",
    gradient: "linear-gradient(90deg, #019EDB 0%, #00CF9C 100%)",
  },
  {
    value: "Grayscale",
    gradient: "linear-gradient(90deg, #f0f0f0 0%, #404040 100%)",
  },
  {
    value: "Custom (per-bar)",
    gradient:
      "linear-gradient(90deg, #e57373 0%, #81c784 33%, #64b5f6 66%, #ffd54f 100%)",
  },
];

const TOGGLE_BUTTON_SX = {
  width: 32,
  height: 32,
  minWidth: 32,
  border: "1px solid #e0e0e0",
  "&.Mui-selected": {
    backgroundColor: "#1976d2",
    color: "white",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
};

interface BarColorEntry {
  name: string;
  displayName: string;
  fallbackColor: string;
}

interface StylingSectionProps {
  title: string;
  subtitle: string;
  titleBold: boolean;
  titleUnderline: boolean;
  colorPalette: string;
  labelsPlacement: "next" | "none";
  showLegend: boolean;
  truncateCategoryText: boolean;
  showValueAxis: boolean;
  compactMode: boolean;
  // Per-bar color props (only used when colorPalette === "Custom (per-bar)")
  barColorEntries?: BarColorEntry[];
  manualColors?: Record<string, string>;
  onManualColorChange?: (barName: string, color: string | null) => void;
  onApplyColorToAll?: (color: string) => void;
  onTitleChange: (value: string) => void;
  onSubtitleChange: (value: string) => void;
  onTitleBoldChange: (value: boolean) => void;
  onTitleUnderlineChange: (value: boolean) => void;
  onColorPaletteChange: (value: string) => void;
  onLabelsPlacementChange: (value: "next" | "none") => void;
  onShowLegendChange: (value: boolean) => void;
  onTruncateCategoryTextChange: (value: boolean) => void;
  onShowValueAxisChange: (value: boolean) => void;
  onCompactModeChange: (value: boolean) => void;
}

const StylingSection: React.FC<StylingSectionProps> = ({
  title,
  subtitle,
  titleBold,
  titleUnderline,
  colorPalette,
  labelsPlacement,
  showLegend,
  truncateCategoryText,
  showValueAxis,
  compactMode,
  barColorEntries,
  manualColors,
  onManualColorChange,
  onApplyColorToAll,
  onTitleChange,
  onSubtitleChange,
  onTitleBoldChange,
  onTitleUnderlineChange,
  onColorPaletteChange,
  onLabelsPlacementChange,
  onShowLegendChange,
  onTruncateCategoryTextChange,
  onShowValueAxisChange,
  onCompactModeChange,
}) => {
  // Tracks the manualColor value (or null) each bar had BEFORE the user started
  // the current edit session — same pattern as LineChartConfigDrawer's originalColors.
  const [pendingOriginals, setPendingOriginals] = useState<
    Record<string, string | null>
  >({});

  // Clear pending state when switching away from Custom (per-bar)
  useEffect(() => {
    if (colorPalette !== "Custom (per-bar)") {
      setPendingOriginals({});
    }
  }, [colorPalette]);

  const isMultiColorPalette =
    colorPalette === "Multicolor" ||
    colorPalette === "Gradient" ||
    colorPalette === "Grayscale" ||
    colorPalette === "Custom (per-bar)";

  return (
    <Accordion
      defaultExpanded
      sx={{ boxShadow: "none", border: "1px solid #eee" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Styling & Customization
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Title & Subtitle */}
          <Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ position: "relative" }}>
                <HPInput
                  label="Chart title"
                  value={title}
                  onChange={(e) => onTitleChange(e.target.value)}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "70%",
                    right: 0,
                    display: "flex",
                    gap: 0.5,
                  }}
                >
                  <ToggleButton
                    value="bold"
                    selected={titleBold}
                    onChange={() => onTitleBoldChange(!titleBold)}
                    size="small"
                    sx={TOGGLE_BUTTON_SX}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton
                    value="underline"
                    selected={titleUnderline}
                    onChange={() => onTitleUnderlineChange(!titleUnderline)}
                    size="small"
                    sx={TOGGLE_BUTTON_SX}
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </ToggleButton>
                </Box>
              </Box>

              <HPInput
                label="Subtitle"
                value={subtitle}
                onChange={(e) => onSubtitleChange(e.target.value)}
                placeholder="Optional"
              />
            </Box>
          </Box>

          {/* Color Palette */}
          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Color palette
            </FormLabel>
            <Select
              value={colorPalette}
              onChange={(e) => onColorPaletteChange(e.target.value)}
              renderValue={(value) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "space-between",
                    flexDirection: "row-reverse",
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 12,
                      borderRadius: "16px",
                      background:
                        COLOR_PREVIEW_MAP[value as string] || "#57bfe7",
                    }}
                  />
                  {value}
                </Box>
              )}
            >
              {COLOR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        backgroundColor: option.color,
                        background: option.gradient || option.color,
                      }}
                    />
                    {option.value}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Per-bar color pickers (Custom per-bar palette only) */}
          {colorPalette === "Custom (per-bar)" &&
            barColorEntries &&
            barColorEntries.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {onApplyColorToAll && (
                  <ApplyColorToAll
                    onApply={onApplyColorToAll}
                    defaultColor={
                      manualColors?.[barColorEntries[0].name] ||
                      barColorEntries[0].fallbackColor
                    }
                    appliedColors={barColorEntries.map(
                      (bar) => manualColors?.[bar.name] || bar.fallbackColor,
                    )}
                  />
                )}
                {barColorEntries.map((bar) => {
                  const currentColor =
                    manualColors?.[bar.name] || bar.fallbackColor;
                  // isDirty only while the user has a pending (unconfirmed) change
                  const isDirty = bar.name in pendingOriginals;
                  return (
                    <Box
                      key={bar.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "12px",
                        }}
                      >
                        {bar.displayName}
                      </Typography>
                      <input
                        type="color"
                        value={currentColor}
                        onChange={(e) => {
                          // Store the original before the first change so Cancel can revert
                          if (!(bar.name in pendingOriginals)) {
                            setPendingOriginals((prev) => ({
                              ...prev,
                              [bar.name]: manualColors?.[bar.name] ?? null,
                            }));
                          }
                          // Update Redux immediately for real-time chart preview
                          onManualColorChange?.(bar.name, e.target.value);
                        }}
                        style={{
                          width: 32,
                          height: 24,
                          cursor: "pointer",
                          border: isDirty
                            ? "2px solid #1976d2"
                            : "1px solid #ccc",
                          borderRadius: 4,
                          padding: 0,
                        }}
                      />
                      {isDirty && (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            title="Apply color"
                            onClick={() =>
                              setPendingOriginals((prev) => {
                                const next = { ...prev };
                                delete next[bar.name];
                                return next;
                              })
                            }
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Cancel"
                            onClick={() => {
                              const original = pendingOriginals[bar.name];
                              // Revert Redux to what it was before editing
                              onManualColorChange?.(bar.name, original);
                              setPendingOriginals((prev) => {
                                const next = { ...prev };
                                delete next[bar.name];
                                return next;
                              });
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

          {/* Labels Placement */}
          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Labels placement
            </FormLabel>
            <Select
              value={labelsPlacement}
              onChange={(e) =>
                onLabelsPlacementChange(e.target.value as "next" | "none")
              }
            >
              <MenuItem value="next">Next to bars</MenuItem>
              <MenuItem value="none">Don't show labels</MenuItem>
            </Select>
          </FormControl>

          {/* Show Legend */}
          {isMultiColorPalette && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography>Show Legend</Typography>
                <Tooltip title="Replace category text with a color legend">
                  <InfoOutline sx={{ fontSize: 16, color: "text.secondary" }} />
                </Tooltip>
              </Box>
              <IOSSwitch
                checked={showLegend}
                onChange={(e) => onShowLegendChange(e.target.checked)}
                size="small"
              />
            </Box>
          )}

          {/* Truncate Category Text */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography>Truncate category text</Typography>
              <Tooltip title="Truncate category text at 40 characters">
                <InfoOutline sx={{ fontSize: 16, color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <IOSSwitch
              checked={truncateCategoryText}
              onChange={(e) => onTruncateCategoryTextChange(e.target.checked)}
              size="small"
            />
          </Box>

          {/* Show Value Axis */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography>Show value axis</Typography>
            <IOSSwitch
              checked={showValueAxis}
              onChange={(e) => onShowValueAxisChange(e.target.checked)}
              size="small"
            />
          </Box>

          {/* Compact Mode */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography>Compact mode</Typography>
              <Tooltip title="Makes bars tighter, recommended for cases with 12 or more bars">
                <InfoOutline sx={{ fontSize: 16, color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <IOSSwitch
              checked={compactMode}
              onChange={(e) => onCompactModeChange(e.target.checked)}
              size="small"
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default StylingSection;
